import React, { useCallback, useEffect, useRef } from 'react'

import { AIMS } from '@disco/constants/FeatureTypes'
import {
  MAX_SEGMENT_LENGTH_SECONDS_AIMS,
  MIN_SEGMENT_LENGTH_SECONDS,
} from '@disco/constants/search'
import {
  applySegmentAndSearch,
  resetSegment,
  selectSearchPageIsLoading,
  selectSegmentEnd,
  selectSegmentStart,
  selectShowApplyButton,
  setSegment,
} from '@disco/state-management/reducers/search-page'
import { useFeatureEnabled } from '@disco/state-management/selectors/business'

import { useSegmentSelectionEnabled } from 'views/pages/search/utils/use-segment-selection'

import { useDispatch, useSelector } from 'hooks/use-redux'

import SegmentSelector from './index'

type Props = {
  url: string
  duration: number
  onSegmentPlayback?: (start: number, end: number) => void
}

/**
 * Container component that manages segment selection state and renders
 * the SegmentSelector when the feature is enabled.
 *
 * Handles:
 * - Feature flag check
 * - Redux state (segmentStart/segmentEnd)
 * - Resetting segment bounds when a new track loads
 */
const SegmentSelectorContainer = ({
  url,
  duration,
  onSegmentPlayback,
}: Props) => {
  const dispatch = useDispatch()
  const isSegmentEnabled = useSegmentSelectionEnabled()
  const segmentStart = useSelector(selectSegmentStart)
  const segmentEnd = useSelector(selectSegmentEnd)
  const showApplyButton = useSelector(selectShowApplyButton)
  const isApplying = useSelector(selectSearchPageIsLoading)
  const aimsEnabled = useFeatureEnabled(AIMS)

  // For AIMS: max segment is 60s; for our sim search: max is track duration
  const maxSegmentLength = aimsEnabled
    ? MAX_SEGMENT_LENGTH_SECONDS_AIMS
    : duration

  // One reset per URL — avoids firing on every render
  const segmentResetForUrlRef = useRef<string | null>(null)

  // Reset segment bounds when duration becomes available for a new track
  useEffect(() => {
    if (
      isSegmentEnabled &&
      duration > 0 &&
      url &&
      segmentResetForUrlRef.current !== url
    ) {
      segmentResetForUrlRef.current = url
      dispatch(resetSegment({ duration }))
    }
  }, [isSegmentEnabled, duration, url, dispatch])

  const clampStart = useCallback(
    (start: number) =>
      Math.max(0, Math.min(start, duration - MIN_SEGMENT_LENGTH_SECONDS)),
    [duration],
  )

  const clampSegment = useCallback(
    (start: number, end: number) => {
      const clampedStart = clampStart(start)
      const clampedEnd = Math.min(
        Math.max(end, clampedStart + MIN_SEGMENT_LENGTH_SECONDS),
        clampedStart + maxSegmentLength,
        duration,
      )
      return { clampedStart, clampedEnd }
    },
    [clampStart, duration, maxSegmentLength],
  )

  const handleSegmentChange = useCallback(
    (start: number, end: number) => {
      const { clampedStart, clampedEnd } = clampSegment(start, end)
      dispatch(setSegment({ start: clampedStart, end: clampedEnd, duration }))
    },
    [clampSegment, dispatch, duration],
  )
  const handleSegmentCommit = useCallback(
    (start: number, end: number) => {
      onSegmentPlayback?.(clampStart(start), end)
    },
    [clampStart, onSegmentPlayback],
  )

  const handleApply = useCallback(() => {
    dispatch(applySegmentAndSearch())
  }, [dispatch])

  if (!isSegmentEnabled || duration < MIN_SEGMENT_LENGTH_SECONDS) {
    return null
  }

  return (
    <SegmentSelector
      segmentStart={segmentStart}
      segmentEnd={segmentEnd}
      duration={duration}
      maxSegmentLength={maxSegmentLength}
      onSegmentChange={handleSegmentChange}
      onSegmentCommit={handleSegmentCommit}
      showApplyButton={showApplyButton}
      isApplying={isApplying}
      onApply={handleApply}
    />
  )
}

export default SegmentSelectorContainer
